import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import {
  artifactDownloadFileName,
  getArtifactCopyText,
  isPointerArtifactType,
} from '../shared/artifactRegistry';

type ArtifactCopyDownloadButtonsProps = {
  /** Artifact MIME type used for the download filename extension. */
  type: string;
  /** Artifact body to copy or download. */
  content: string;
  /** Human-readable artifact title used for the download filename. */
  title: string;
  /** Disables copy and download (streaming or missing content). Pointer download and type-specific copy are handled internally. */
  disabled?: boolean;
};

/**
 * Triggers a browser download of artifact content.
 * @param type Artifact MIME type.
 * @param content Artifact body.
 * @param title Human-readable artifact title.
 */
function downloadArtifactContent(type: string, content: string, title: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = artifactDownloadFileName(type, title);
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Copy-to-clipboard and download icon buttons shown next to each other on artifact surfaces.
 */
export default function ArtifactCopyDownloadButtons({
  type,
  content,
  title,
  disabled = false,
}: ArtifactCopyDownloadButtonsProps): React.ReactElement {
  'use memo';

  const { t } = useTranslation();
  const { snack } = useSnack();
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const copyText = getArtifactCopyText(type, content);
  const copyDisabled = disabled || copyText === null;
  const downloadDisabled = disabled || isPointerArtifactType(type) || content.length === 0;

  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== undefined) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const handleCopy = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (copyText === null) {
      return;
    }
    void navigator.clipboard.writeText(copyText).then(
      () => {
        setCopied(true);
        if (copyTimerRef.current !== undefined) {
          clearTimeout(copyTimerRef.current);
        }
        copyTimerRef.current = setTimeout(() => setCopied(false), 3000);
      },
      () => {
        snack.error(t('artifact-ui:artifactCopyFailed'));
      },
    );
  };

  const handleDownload = (event: React.MouseEvent): void => {
    event.stopPropagation();
    downloadArtifactContent(type, content, title);
  };

  return (
    <Box className="artifact-copy-download-buttons" sx={{ display: 'flex', alignItems: 'center' }}>
      <PowerIconButton
        size="small"
        padding={2}
        sx={{ my: -2, color: copied ? 'success.main' : undefined }}
        icon={`clipboard${copied ? '-check' : ''}`}
        title={copied ? t('artifact-ui:artifactCopied') : t('artifact-ui:artifactCopy')}
        disabled={copyDisabled}
        onClick={handleCopy}
      />
      <PowerIconButton
        size="small"
        padding={2}
        sx={{ my: -2 }}
        icon="down-to-bracket"
        title={t('artifact-ui:artifactDownload')}
        disabled={downloadDisabled}
        onClick={handleDownload}
      />
    </Box>
  );
}
